import React, { useMemo } from "react";
import { getSpacing } from "../../../designTokens";
import { useExportProgress } from "../../../context/ExportProgressContext";
import { ExportStatusModal } from "../../AgentPages/Configuration/components/ExportData/ExportStatusModal";
import { ExportHistoryModal } from "../../AgentPages/Configuration/components/ExportData/ExportHistoryModal";
import {
  getExportEntityLabel,
  getExportHistoryApi,
  getExportStatusApi,
} from "../../../utils/exportApiHandlers";
import { isInFlightExportStatus } from "../../../utils/exportProgressStorage";
import { canDownloadInApp } from "../../../utils/permissionAccess";
import { useAppSelector } from "../../../redux/hooks";
import { ExportMinimizedTile } from "./ExportMinimizedTile";

export const ExportProgressHost: React.FC = () => {
  const {
    jobs,
    historyViewer,
    minimizeExport,
    closeExport,
    expandExport,
    dismissExport,
    openExportHistory,
    closeExportHistory,
  } = useExportProgress();
  const userProfile = useAppSelector((state) => state.userProfile);
  const canDownload = canDownloadInApp(userProfile);

  const expandedJob = jobs.find((job) => job.presentation === "expanded");
  const minimizedJobs = jobs.filter(
    (job) =>
      job.presentation === "minimized" &&
      (isInFlightExportStatus(job.status) ||
        job.status === "COMPLETED" ||
        job.status === "FAILED")
  );

  const historyTarget = historyViewer;
  const historyApi = useMemo(() => {
    if (!historyTarget) return undefined;
    return getExportHistoryApi(historyTarget.entityType, historyTarget.source);
  }, [historyTarget]);
  const historyStatusApi = useMemo(() => {
    if (!historyTarget) return undefined;
    return getExportStatusApi(historyTarget.entityType, historyTarget.source);
  }, [historyTarget]);

  return (
    <>
      {expandedJob ? (
        <ExportStatusModal
          isOpen
          exportId={expandedJob.exportId}
          analyticsEntity={undefined}
          status={expandedJob.status}
          processedCount={expandedJob.processedCount}
          completedRecord={expandedJob.completedRecord ?? null}
          errorMessage={expandedJob.errorMessage ?? null}
          isPolling={isInFlightExportStatus(expandedJob.status)}
          entityLabel={expandedJob.entityLabel}
          recipientEmail={expandedJob.recipientEmail ?? null}
          columnCount={expandedJob.columnCount}
          deliveryMode={expandedJob.deliveryMode}
          canDownloadInApp={expandedJob.canDownloadInApp}
          onMinimize={() => minimizeExport(expandedJob.exportId)}
          onClose={() => {
            if (isInFlightExportStatus(expandedJob.status)) {
              closeExport(expandedJob.exportId);
              return;
            }
            dismissExport(expandedJob.exportId);
          }}
          onViewHistory={() =>
            openExportHistory({
              entityType: expandedJob.entityType,
              source: expandedJob.source,
              entityLabel: expandedJob.entityLabel,
              focusExportId: expandedJob.exportId,
            })
          }
          idPrefix={`${expandedJob.entityType}-export-status`}
        />
      ) : null}

      {minimizedJobs.length > 0 ? (
        <div
          id="export-progress-tile-dock"
          style={{
            position: "fixed",
            left: "50%",
            bottom: getSpacing("24px"),
            transform: "translateX(-50%)",
            zIndex: 9990,
            display: "flex",
            flexDirection: "column",
            gap: getSpacing("8px"),
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          {minimizedJobs.map((job) => (
            <div key={job.exportId} style={{ pointerEvents: "auto" }}>
              <ExportMinimizedTile
                job={job}
                onExpand={() => expandExport(job.exportId)}
              />
            </div>
          ))}
          <style>{`@keyframes export-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : null}

      {historyTarget && historyApi ? (
        <ExportHistoryModal
          isOpen
          onClose={closeExportHistory}
          fetchHistory={historyApi}
          fetchExportById={historyStatusApi}
          entityLabel={
            historyTarget.entityLabel ||
            getExportEntityLabel(historyTarget.entityType)
          }
          canDownloadInApp={canDownload}
          highlightExportId={historyTarget.focusExportId}
          idPrefix={`${historyTarget.entityType}-export-history`}
        />
      ) : null}
    </>
  );
};

export default ExportProgressHost;
